import { useEffect, useMemo, useState } from 'react'
import {
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material'
import { Button } from 'components'
import intl from 'localization/components'
import { useSnackbar } from 'notistack'
import { useIntl } from 'react-intl'
import { useCreateZone } from 'services/queries/use-create-zone'
import { useUpdateZone } from 'services/queries/use-update-zone'
import { countries as Countries } from 'utils/constants'
import { Zone } from 'types'

const MAX_NAME_LENGTH = 255

type CreateOrEditZoneProps = {
  open: boolean
  onClose: () => void
  // All existing zones — used both as composition sources and to guard
  // against duplicate names.
  existingZones: Zone[]
  // When set, the dialog is in edit mode for this zone.
  zoneToEdit?: Zone
}

type CountryOption = {
  code: string
  name: string
}

const CreateOrEditZone = ({
  open,
  onClose,
  existingZones,
  zoneToEdit,
}: CreateOrEditZoneProps): JSX.Element => {
  const reactIntl = useIntl()
  const messages = reactIntl.messages
  const { enqueueSnackbar } = useSnackbar()
  const isEdit = Boolean(zoneToEdit)

  const [name, setName] = useState('')
  const [selectedZoneNames, setSelectedZoneNames] = useState<string[]>([])
  const [selectedCountryCodes, setSelectedCountryCodes] = useState<string[]>([])

  const t = (key: string) => messages[intl.customerZones(key)] as string

  const onSuccess = () => {
    enqueueSnackbar(
      isEdit
        ? t('custom-zone-update-success')
        : t('custom-zone-create-success'),
      { variant: 'success', autoHideDuration: 3000 },
    )
    onClose()
  }

  const onError = () => {
    enqueueSnackbar(
      isEdit ? t('custom-zone-update-failed') : t('custom-zone-create-failed'),
      { variant: 'error', autoHideDuration: 3000 },
    )
  }

  const { mutate: createZone, isLoading: isCreating } = useCreateZone({
    onSuccess,
    onError,
  })
  const { mutate: updateZone, isLoading: isUpdating } = useUpdateZone({
    onSuccess,
    onError,
  })
  const isSaving = isCreating || isUpdating

  // Reset / prefill whenever the dialog opens or the edit target changes.
  useEffect(() => {
    if (!open) return
    if (zoneToEdit) {
      setName(zoneToEdit.name)
      setSelectedCountryCodes(
        Array.from(new Set(zoneToEdit.countries.map((c) => c.toUpperCase()))),
      )
      setSelectedZoneNames([])
    } else {
      setName('')
      setSelectedCountryCodes([])
      setSelectedZoneNames([])
    }
  }, [open, zoneToEdit])

  // Full ISO country list, alphabetized by name, for the individual picker.
  const countryOptions: CountryOption[] = useMemo(() => {
    const names = Countries.getNames('en', { select: 'official' }) as Record<
      string,
      string
    >
    return Object.keys(names)
      .map((code) => ({ code, name: names[code] }))
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [])

  // Existing zones available as composition sources (a zone can't include
  // itself), alphabetized.
  const zoneOptions: string[] = useMemo(
    () =>
      existingZones
        .map((z) => z.name)
        .filter((n) => !zoneToEdit || n !== zoneToEdit.name)
        .sort((a, b) => a.localeCompare(b)),
    [existingZones, zoneToEdit],
  )

  // The resolved, de-duplicated country set: union of every selected zone's
  // countries plus the individually selected countries, sorted by name.
  const resolvedCountries: string[] = useMemo(() => {
    const fromZones = selectedZoneNames.flatMap(
      (zoneName) =>
        existingZones.find((z) => z.name === zoneName)?.countries ?? [],
    )
    const all = [...fromZones, ...selectedCountryCodes].map((c) =>
      c.toUpperCase(),
    )
    return Array.from(new Set(all)).sort((a, b) => {
      const nameA = Countries.getName(a, 'en') || a
      const nameB = Countries.getName(b, 'en') || b
      return nameA.localeCompare(nameB)
    })
  }, [selectedZoneNames, selectedCountryCodes, existingZones])

  const trimmedName = name.trim()
  const isDuplicateName = existingZones.some(
    (z) =>
      z.name.toLowerCase() === trimmedName.toLowerCase() &&
      (!zoneToEdit || z.name !== zoneToEdit.name),
  )

  let nameError: string | null = null
  if (trimmedName.length === 0) {
    nameError = t('custom-zone-name-required')
  } else if (isDuplicateName) {
    nameError = t('custom-zone-name-duplicate')
  }

  const canSubmit = !nameError && resolvedCountries.length > 0 && !isSaving

  const handleSubmit = () => {
    if (!canSubmit) return
    if (zoneToEdit) {
      const updateData: { name?: string; countries: string[] } = {
        countries: resolvedCountries,
      }
      // RisikaAPI rejects a rename to the same value, so only send a new name
      // when it actually changed.
      if (trimmedName !== zoneToEdit.name) {
        updateData.name = trimmedName
      }
      updateZone({ name: zoneToEdit.name, update_data: updateData })
    } else {
      createZone({ name: trimmedName, countries: resolvedCountries })
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isEdit ? t('custom-zone-edit-title') : t('custom-zone-create-title')}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'grid', gap: '20px', pt: 1 }}>
          <TextField
            label={t('custom-zone-name-label')}
            value={name}
            disabled={isSaving}
            error={Boolean(trimmedName) && Boolean(nameError)}
            helperText={
              (trimmedName && nameError) || t('custom-zone-name-helper')
            }
            inputProps={{ maxLength: MAX_NAME_LENGTH }}
            onChange={(e) => setName(e.target.value)}
            fullWidth
          />

          <Autocomplete
            multiple
            options={zoneOptions}
            value={selectedZoneNames}
            disabled={isSaving}
            onChange={(_e, value) => setSelectedZoneNames(value)}
            renderInput={(params) => (
              <TextField {...params} label={t('custom-zone-zones-label')} />
            )}
          />

          <Autocomplete
            multiple
            options={countryOptions}
            value={countryOptions.filter((o) =>
              selectedCountryCodes.includes(o.code),
            )}
            disabled={isSaving}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, value) => option.code === value.code}
            onChange={(_e, value) =>
              setSelectedCountryCodes(value.map((o) => o.code))
            }
            renderInput={(params) => (
              <TextField {...params} label={t('custom-zone-countries-label')} />
            )}
          />

          <Box>
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 600,
                color: 'text.secondary',
                mb: 1,
              }}
            >
              {t('custom-zone-preview-label')} ({resolvedCountries.length})
            </Typography>
            {resolvedCountries.length === 0 ? (
              <Typography sx={{ fontSize: 13, color: 'text.disabled' }}>
                {t('custom-zone-preview-empty')}
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {resolvedCountries.map((code) => (
                  <Chip
                    key={code}
                    size="small"
                    label={Countries.getName(code, 'en') || code}
                  />
                ))}
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Box sx={{ padding: '10px 20px', display: 'flex', gap: '10px' }}>
          <Button onClick={onClose} disabled={isSaving}>
            {messages[intl.confirmAction('cancel')] as string}
          </Button>
          <Button onClick={handleSubmit} color="primary" disabled={!canSubmit}>
            {isSaving ? (
              <CircularProgress size={24} />
            ) : (
              (messages[
                isEdit
                  ? intl.confirmAction('update')
                  : intl.confirmAction('add')
              ] as string)
            )}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  )
}

export default CreateOrEditZone
